import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Pencil, Trash2, Loader2, X, Check } from "lucide-react";
import { toast } from "sonner";
import { api, mediaUrl, formatApiError } from "@/lib/api";
import type { EntityConfig, FieldDef } from "@/lib/entities";
import { ImageUploader } from "@/components/cms/ImageUploader";

type Row = Record<string, any>;
type OptionMap = Record<string, { id: string; label: string }[]>;

function initialForm(config: EntityConfig): Row {
  const f: Row = {};
  config.fields.forEach((fd) => {
    f[fd.name] = fd.defaultValue ?? (fd.type === "boolean" ? false : fd.type === "number" ? 0 : "");
  });
  return f;
}

export const CrudManager: React.FC<{ config: EntityConfig }> = ({ config }) => {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [options, setOptions] = useState<OptionMap>({});
  const [optionsReady, setOptionsReady] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Row | null>(null);
  const [form, setForm] = useState<Row>(initialForm(config));
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const tableFields = useMemo(
    () => config.fields.filter((f) => f.showInTable),
    [config]
  );

  const load = useCallback(async () => {
    setRows(null);
    const { data } = await api.get(config.endpoint);
    setRows(data);
  }, [config.endpoint]);

  const loadOptions = useCallback(async () => {
    const endpoints = Array.from(
      new Set(config.fields.filter((f) => f.optionsEndpoint).map((f) => f.optionsEndpoint!))
    );
    if (endpoints.length === 0) {
      setOptionsReady(true);
      return;
    }
    const result: OptionMap = {};
    await Promise.all(
      endpoints.map(async (ep) => {
        try {
          const { data } = await api.get(ep);
          result[ep] = data.map((d: Row) => ({ id: d.id, label: d.name ?? d.title ?? d.id }));
        } catch {
          result[ep] = [];
        }
      })
    );
    setOptions(result);
    setOptionsReady(true);
  }, [config]);

  useEffect(() => {
    load();
    loadOptions();
  }, [load, loadOptions]);

  const labelFor = (field: FieldDef, value: any): string => {
    if (!field.optionsEndpoint) return String(value ?? "");
    const opt = options[field.optionsEndpoint]?.find((o) => o.id === value);
    return opt?.label ?? "—";
  };

  const openCreate = () => {
    setEditing(null);
    setForm(initialForm(config));
    setOpen(true);
  };

  const openEdit = (row: Row) => {
    setEditing(row);
    const f: Row = {};
    config.fields.forEach((fd) => {
      f[fd.name] = row[fd.name] ?? (fd.type === "boolean" ? false : fd.type === "number" ? 0 : "");
    });
    setForm(f);
    setOpen(true);
  };

  const save = async () => {
    for (const fd of config.fields) {
      if (fd.required && !String(form[fd.name] ?? "").trim()) {
        toast.error(`Campo obbligatorio: ${fd.label}`);
        return;
      }
    }
    const payload: Row = {};
    config.fields.forEach((fd) => {
      let v = form[fd.name];
      if (fd.type === "number") v = Number(v) || 0;
      if (fd.type === "select" && v === "") v = null;
      payload[fd.name] = v;
    });
    setSaving(true);
    try {
      if (editing) {
        await api.put(`${config.endpoint}/${editing.id}`, payload);
        toast.success("Modifiche salvate");
      } else {
        await api.post(config.endpoint, payload);
        toast.success("Elemento creato");
      }
      setOpen(false);
      await load();
    } catch (err: any) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`${config.endpoint}/${deleteId}`);
      toast.success("Elemento eliminato");
      setDeleteId(null);
      await load();
    } catch (err: any) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const renderCell = (field: FieldDef, row: Row) => {
    const v = row[field.name];
    if (field.type === "boolean")
      return v ? (
        <span className="text-secondary"><Check className="h-4 w-4" /></span>
      ) : (
        <span className="text-muted-foreground"><X className="h-4 w-4" /></span>
      );
    if (field.type === "image")
      return v ? (
        <img src={mediaUrl(v)} alt="" className="h-9 w-9 object-cover border border-white/15" />
      ) : (
        <span className="text-muted-foreground text-xs">—</span>
      );
    if (field.type === "select") return <span>{labelFor(field, v)}</span>;
    return <span>{String(v ?? "")}</span>;
  };

  return (
    <div data-testid={`crud-${config.key}`}>
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-secondary mb-2">CMS</p>
          <h1 className="font-display font-black tracking-tighter text-4xl">{config.title}</h1>
          <p className="text-muted-foreground text-sm mt-1 max-w-xl">{config.subtitle}</p>
        </div>
        <button
          data-testid={`${config.key}-new-button`}
          onClick={openCreate}
          disabled={!optionsReady}
          className="shrink-0 flex items-center gap-2 bg-primary text-primary-foreground font-bold uppercase tracking-[0.12em] text-xs px-5 py-3 hover:-translate-y-[2px] transition-transform disabled:opacity-50 disabled:hover:translate-y-0"
        >
          {optionsReady ? <Plus className="h-4 w-4" /> : <Loader2 className="h-4 w-4 animate-spin" />} Nuovo
        </button>
      </div>

      {rows === null ? (
        <div className="py-20 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-white/15 py-16 text-center text-muted-foreground" data-testid={`${config.key}-empty`}>
          Nessun elemento. Clicca "Nuovo" per crearne uno.
        </div>
      ) : (
        <div className="border border-white/10 overflow-x-auto">
          <table className="w-full text-sm" data-testid={`${config.key}-table`}>
            <thead>
              <tr className="bg-white/5 text-left">
                {tableFields.map((f) => (
                  <th key={f.name} className="px-4 py-3 text-[11px] uppercase tracking-wider font-bold text-muted-foreground">
                    {f.label}
                  </th>
                ))}
                <th className="px-4 py-3 text-right text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-t border-white/10 hover:bg-white/[0.03]" data-testid={`${config.key}-row`}>
                  {tableFields.map((f) => (
                    <td key={f.name} className="px-4 py-3 align-middle">{renderCell(f, row)}</td>
                  ))}
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        data-testid={`${config.key}-edit-button`}
                        onClick={() => openEdit(row)}
                        disabled={!optionsReady}
                        className="p-2 text-muted-foreground hover:text-secondary transition-colors disabled:opacity-40"
                        title="Modifica"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        data-testid={`${config.key}-delete-button`}
                        onClick={() => setDeleteId(row.id)}
                        className="p-2 text-muted-foreground hover:text-primary transition-colors"
                        title="Elimina"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit dialog */}
      {open && (
        <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/70 p-4 py-10" data-testid={`${config.key}-dialog`}>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-lg bg-card border border-white/15 p-8"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display font-bold text-2xl">
                {editing ? "Modifica" : "Nuovo"} · {config.title}
              </h2>
              <button data-testid={`${config.key}-dialog-close`} onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-5 max-h-[60vh] overflow-y-auto pr-1">
              {config.fields.map((fd) => (
                <FieldInput
                  key={fd.name}
                  field={fd}
                  value={form[fd.name]}
                  options={fd.optionsEndpoint ? options[fd.optionsEndpoint] ?? [] : []}
                  onChange={(v) => setForm((p) => ({ ...p, [fd.name]: v }))}
                  entityKey={config.key}
                />
              ))}
            </div>
            <div className="flex gap-3 mt-8">
              <button
                data-testid={`${config.key}-save-button`}
                onClick={save}
                disabled={saving}
                className="flex-1 bg-primary text-primary-foreground font-bold uppercase tracking-[0.12em] text-xs py-3 flex items-center justify-center gap-2 hover:-translate-y-[2px] transition-transform disabled:opacity-60"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}
              </button>
              <button
                onClick={() => setOpen(false)}
                className="px-5 py-3 border border-white/15 text-xs uppercase tracking-[0.12em] font-bold text-muted-foreground hover:text-foreground transition-colors"
              >
                Annulla
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteId && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4" data-testid={`${config.key}-delete-dialog`}>
          <div className="w-full max-w-sm bg-card border border-white/15 p-8">
            <h3 className="font-display font-bold text-xl mb-2">Conferma eliminazione</h3>
            <p className="text-sm text-muted-foreground mb-6">Questa azione non può essere annullata.</p>
            <div className="flex gap-3">
              <button
                data-testid={`${config.key}-confirm-delete-button`}
                onClick={doDelete}
                className="flex-1 bg-primary text-primary-foreground font-bold uppercase tracking-[0.12em] text-xs py-3"
              >
                Elimina
              </button>
              <button
                onClick={() => setDeleteId(null)}
                className="px-5 py-3 border border-white/15 text-xs uppercase tracking-[0.12em] font-bold text-muted-foreground"
              >
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const FieldInput: React.FC<{
  field: FieldDef;
  value: any;
  options: { id: string; label: string }[];
  onChange: (v: any) => void;
  entityKey: string;
}> = ({ field, value, options, onChange, entityKey }) => {
  const testid = `${entityKey}-field-${field.name}`;
  const labelEl = (
    <label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground block mb-2">
      {field.label} {field.required && <span className="text-primary">*</span>}
    </label>
  );

  if (field.type === "boolean")
    return (
      <div className="flex items-center justify-between border border-white/10 px-4 py-3">
        <span className="text-sm">{field.label}</span>
        <button
          type="button"
          data-testid={testid}
          onClick={() => onChange(!value)}
          className={`relative h-6 w-11 transition-colors ${value ? "bg-secondary" : "bg-white/15"}`}
        >
          <span className={`absolute top-1 h-4 w-4 bg-black transition-all ${value ? "left-6" : "left-1"}`} />
        </button>
      </div>
    );

  if (field.type === "image")
    return (
      <div>
        {labelEl}
        <ImageUploader value={value} onChange={onChange} testid={testid} />
      </div>
    );

  if (field.type === "textarea")
    return (
      <div>
        {labelEl}
        <textarea
          data-testid={testid}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          rows={4}
          className="w-full border border-input bg-black/50 py-2.5 px-3 text-sm outline-none focus:border-primary transition-colors"
        />
        {field.helper && <p className="text-[11px] text-muted-foreground mt-1">{field.helper}</p>}
      </div>
    );

  if (field.type === "select")
    return (
      <div>
        {labelEl}
        <select
          data-testid={testid}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full border border-input bg-black/50 py-2.5 px-3 text-sm outline-none focus:border-primary transition-colors"
        >
          <option value="">— Nessuno —</option>
          {options.map((o) => (
            <option key={o.id} value={o.id}>{o.label}</option>
          ))}
        </select>
      </div>
    );

  return (
    <div>
      {labelEl}
      <input
        data-testid={testid}
        type={field.type === "number" ? "number" : "text"}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-input bg-black/50 py-2.5 px-3 text-sm outline-none focus:border-primary transition-colors"
      />
      {field.helper && <p className="text-[11px] text-muted-foreground mt-1">{field.helper}</p>}
    </div>
  );
};
